/* eslint-disable @typescript-eslint/no-floating-promises */
import type { Collection } from '#~/api/device/registry/index.js';
import { useAuth0 } from '#~/auth/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { getBrands, getModels, getTemplate } from '#~/device/registry/commands/index.js';
import {
  getRegistryRequestScope,
  scopedTemplateKey,
  subscribeRegistryOrganizationScope
} from '#~/device/registry/registry-config-cache.js';
import type { Template } from '#~/device/registry/index.js';
import { raiseError } from '#~/notifications/index.js';
import type { Brand, Model } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { isNil } from '@tryiris-ai/js-types';
import { useEffect, useSyncExternalStore } from 'react';

/**
 * Asynchronously returns the brands in the device registry. If the brands have not yet been
 * fetched, the hook will return `undefined` and the brands will be fetched in the background.
 *
 * @returns The brands in the device registry or `undefined`.
 */
export const useBrands = (): T_Maybe<Collection<Brand>> => {
  const brands = useDepot((depotState) => depotState.registry.brands);
  const { getAccessTokenSilently } = useAuth0();

  // If the set of brands is not available in the depot, trigger a fetch.
  if (isNil(brands)) {
    (async (): Promise<void> => {
      const token = await getAccessTokenSilently();

      // Background registry read on mount: it self-heals on the next fetch and
      // asks nothing of the user, so it must not light the bell (raiseError
      // defaults actionable=false).
      getBrands(token).catch(raiseError);
    })();
  }

  return brands;
};

/**
 * Asynchronously returns the models for the specified brand. If the specified brand is `nil` or if
 * models for the specified brand have not yet been fetched, the hook will return `undefined` and
 * the models will be fetched (if applicable) in the background.
 *
 * @param brandId - The ID of the brand whose models are to be fetched.
 * @returns The models in the device registry or `undefined`.
 */
export const useModels = (brandId: T_Maybe<Brand['id']>): T_Maybe<Collection<Model>> => {
  const models = useDepot((depotState) =>
    brandId ? depotState.registry.models[brandId] : undefined
  );
  const { getAccessTokenSilently } = useAuth0();

  // Was a brand specified?
  if (!brandId) {
    return undefined;
  }

  // If the set of models for this brand is not available in the depot, trigger a fetch.
  if (isNil(models)) {
    (async (): Promise<void> => {
      const token = await getAccessTokenSilently();
      // Background registry read on mount: self-heals on the next fetch, so it
      // must not light the bell (raiseError defaults actionable=false).
      getModels(brandId, token).catch(raiseError);
    })();
  }

  return models;
};

/**
 * Batch sibling of `useModels` — resolves the models for MULTIPLE brands at once. Mirrors
 * `useTemplates` (the per-model batch precedent): reads each brand's models from the depot and
 * triggers a background `getModels` fetch for any brand not yet cached. Returns a map keyed by
 * brand id (a brand maps to `undefined` while its models are still loading).
 *
 * Used by the Multi-Add selector to resolve every DISTINCT brand-detected discovery's generic
 * "Other" model without calling `useModels` in a loop (rules of hooks). Composes the existing
 * `getModels` command — it is not a new registry command.
 *
 * @param brandIds - Brand IDs whose models are to be fetched.
 * @returns A map from brand ID to that brand's models (or `undefined` while loading).
 */
export const useModelsByBrands = (
  brandIds: T_Maybe<Brand['id']>[]
): Record<Brand['id'], T_Maybe<Collection<Model>>> => {
  const models = useDepot((depotState) => {
    if (!brandIds?.length) return {};

    return brandIds.reduce<Record<Brand['id'], T_Maybe<Collection<Model>>>>(
      (modelsByBrandId, brandId) => {
        if (brandId && depotState.registry.models[brandId]) {
          modelsByBrandId[brandId] = depotState.registry.models[brandId];
        }
        return modelsByBrandId;
      },
      {}
    );
  });

  const { getAccessTokenSilently } = useAuth0();

  // Fetch the missing brands from an effect keyed by the missing SET (as `useTemplates` does),
  // not from the render body: a render-time fetch re-queued every still-loading brand behind the
  // shared registry mutex on every parent re-render (O(N²) no-op tasks ahead of user reads).
  const missingKey = JSON.stringify(
    [
      ...new Set(
        (brandIds ?? []).filter(
          (brandId): brandId is Brand['id'] => !!brandId && isNil(models[brandId])
        )
      )
    ].sort()
  );
  useEffect(() => {
    const missingBrandIds = JSON.parse(missingKey) as Brand['id'][];
    if (missingBrandIds.length === 0) return;
    void (async () => {
      const token = await getAccessTokenSilently();

      // Fetch each missing brand's models.
      for (const brandId of missingBrandIds) {
        getModels(brandId, token).catch(raiseError);
      }
    })().catch(raiseError);
  }, [getAccessTokenSilently, missingKey]);

  return models;
};

/**
 * Asynchronously returns the registry template for the specified model. If the model is `nil` or if
 * the template for the specified model has not yet been fetched, the hook will return `undefined`
 * and the template will be fetched (if applicable) in the background.
 *
 * @param modelId - The ID of the model whose registry template is to be fetched.
 * @returns The registry template or `undefined`.
 */
export const useTemplate = (modelId: T_Maybe<Model['id']>): T_Maybe<Template> => {
  const orgScope = useSyncExternalStore(
    subscribeRegistryOrganizationScope,
    getRegistryRequestScope
  );
  const cacheKey = modelId ? scopedTemplateKey(orgScope, modelId) : undefined;
  const template = useDepot((depotState) =>
    cacheKey ? depotState.registry.templates[cacheKey] : undefined
  );
  const { getAccessTokenSilently } = useAuth0();

  // A rejected lookup must settle instead of requesting again on every render.
  // Notification updates otherwise feed the error back into the discovery UI.
  useEffect(() => {
    if (!modelId || modelId.startsWith('transitory:') || !isNil(template)) return;
    void (async () => {
      const token = await getAccessTokenSilently();
      await getTemplate(modelId, token);
    })().catch(raiseError);
  }, [getAccessTokenSilently, modelId, orgScope, template]);

  return modelId?.startsWith('transitory:') ? undefined : template;
};

/**
 * Asynchronously returns the registry templates for the specified models. If a modelId is `nil` or if
 * the template for a specified model has not yet been fetched, the hook will return an empty object
 * for that model and the template will be fetched in the background.
 *
 * @param modelIds - An array of model IDs whose registry templates are to be fetched.
 * @returns An object mapping model IDs to their templates.
 */
export const useTemplates = (
  modelIds: T_Maybe<Model['id']>[]
): Record<Model['id'], T_Maybe<Template>> => {
  const orgScope = useSyncExternalStore(
    subscribeRegistryOrganizationScope,
    getRegistryRequestScope
  );
  const templates = useDepot((depotState) => {
    if (!modelIds?.length) return {};

    return modelIds.reduce<Record<Model['id'], T_Maybe<Template>>>(
      (templatesByModelId, modelId) => {
        if (modelId) {
          const key = scopedTemplateKey(orgScope, modelId);
          if (depotState.registry.templates[key]) {
            templatesByModelId[modelId] = depotState.registry.templates[key];
          }
        }
        return templatesByModelId;
      },
      {}
    );
  });

  const { getAccessTokenSilently } = useAuth0();

  // Discovery emits fresh arrays and can advertise one model several times.
  // Key the effect by the missing IDs, not their array identity or row order,
  // so failed lookups remain settled until the requested set changes/remounts.
  const missingKey = JSON.stringify(
    [
      ...new Set(
        modelIds.filter(
          (modelId): modelId is Model['id'] =>
            !!modelId && !modelId.startsWith('transitory:') && isNil(templates[modelId])
        )
      )
    ].sort()
  );
  useEffect(() => {
    const missingModelIds = JSON.parse(missingKey) as Model['id'][];
    if (missingModelIds.length === 0) return;
    void (async () => {
      const token = await getAccessTokenSilently();
      await Promise.all(
        missingModelIds.map((modelId) => getTemplate(modelId, token).catch(raiseError))
      );
    })().catch(raiseError);
  }, [getAccessTokenSilently, missingKey, orgScope]);

  return templates;
};

/**
 * Asynchronously returns the brand name for the specified model. If the model is `nil` or if
 * the template for the specified model has not yet been fetched, the hook will return `undefined`
 * and the template will be fetched (if applicable) in the background.
 *
 * @param modelId - The ID of the model whose brand name is to be fetched.
 * @returns The brand name or `undefined`.
 */
export const useBrandNameFromModelId = (modelId: T_Maybe<Model['id']>): T_Maybe<string> => {
  const template = useTemplate(modelId);
  const brands = useBrands();

  if (!modelId || !template?.model?.brandId || !brands?.items) {
    return undefined;
  }

  return brands.items[template.model.brandId]?.name;
};
